import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User } from '../../services/authService';
import { cashflowService, CashflowEntry } from '../../services/cashflowService';
import './Cashflow.css';

interface IncomeSpendProps {
  user: User | null;
  onEdit?: (entry: CashflowEntry) => void;
}

interface SubCategoryGroup {
  sub_category: string;
  total: number;
  entries: CashflowEntry[];
}

interface MonthGroup {
  month: string;
  total: number;
  sub_categories: SubCategoryGroup[];
}

const IncomeSpend: React.FC<IncomeSpendProps> = ({ user, onEdit }) => {
  const [allEntries, setAllEntries] = useState<CashflowEntry[]>([]);
  const [monthGroups, setMonthGroups] = useState<MonthGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const oldestEntryDateRef = useRef<Date | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [selectedSubCategory, setSelectedSubCategory] = useState<{ month: string; sub_category: string } | null>(null);

  const loadData = useCallback(async (isInitialLoad: boolean) => {
    if (isInitialLoad) {
      setLoading(true);
      setAllEntries([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const endDate = isInitialLoad ? new Date() : new Date(oldestEntryDateRef.current!.getTime() - 1);
      const startDate = new Date(endDate);
      startDate.setMonth(startDate.getMonth() - (isInitialLoad ? 12 : 6));

      const formatDate = (date: Date) => date.toISOString().split('T')[0];

      // Fetch all entries for the date range in a single API call
      // Use category filter on backend to filter for income only
      const response = await cashflowService.getCashflowEntries({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        category: 'income',
        limit: 10000, // Large limit to fetch all entries in the range
        offset: 0,
      });

      if (!response.success || !response.cashflow) {
        if (!isInitialLoad) {
          setHasMore(false);
        }
        return;
      }

      const newEntries = response.cashflow;

      if (newEntries.length === 0 && !isInitialLoad) {
        setHasMore(false);
        return;
      }

      setAllEntries(prevEntries => {
        const combined = [...prevEntries, ...newEntries];
        const unique = Array.from(new Map(combined.map(e => [e.id, e])).values());
        unique.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        if (unique.length > 0) {
          oldestEntryDateRef.current = new Date(unique[unique.length - 1].created_at);
        }
        return unique;
      });
    } catch (error) {
      console.error('Error loading income data:', error);
      if (!isInitialLoad) {
        setHasMore(false);
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, []);

  const groupByMonth = (entries: CashflowEntry[]): MonthGroup[] => {
    const monthMap: { [key: string]: CashflowEntry[] } = {};

    entries.forEach(entry => {
      try {
        const date = new Date(entry.created_at);
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date for entry:', entry.id, entry.created_at);
          return;
        }
        const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthMap[month]) {
          monthMap[month] = [];
        }
        monthMap[month].push(entry);
      } catch (error) {
        console.warn('Error processing entry date:', entry.id, error);
      }
    });

    // Convert to MonthGroup array and calculate totals
    return Object.keys(monthMap)
      .sort((a, b) => b.localeCompare(a)) // Sort descending (newest first)
      .map(month => {
        const entries = monthMap[month];
        const total = entries.reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

        // Group by sub-category directly (since we're already filtering by category='income')
        const subCategoryMap: { [key: string]: CashflowEntry[] } = {};
        entries.forEach(entry => {
          const subCategory = entry.sub_category || 'Uncategorized';
          if (!subCategoryMap[subCategory]) {
            subCategoryMap[subCategory] = [];
          }
          subCategoryMap[subCategory].push(entry);
        });

        const sub_categories: SubCategoryGroup[] = Object.keys(subCategoryMap)
          .map(subCategory => ({
            sub_category: subCategory,
            total: subCategoryMap[subCategory].reduce((sum, entry) => sum + Math.abs(entry.amount), 0),
            entries: subCategoryMap[subCategory].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          }))
          .sort((a, b) => b.total - a.total); // Sort by amount descending

        return { month, total, sub_categories };
      });
  };

  useEffect(() => {
    if (user) {
      loadData(true);
    }
  }, [user, loadData]);

  useEffect(() => {
    if (allEntries.length > 0) {
      const grouped = groupByMonth(allEntries);
      setMonthGroups(grouped);
    } else if (!loading) {
      setMonthGroups([]);
    }
  }, [allEntries, loading]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadData(false);
    }
  };

  const formatAmount = (amount: number): string => {
    return Math.abs(amount).toLocaleString('en-US');
  };

  const toggleMonth = (month: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(month)) {
        newSet.delete(month);
      } else {
        newSet.add(month);
      }
      return newSet;
    });
  };

  const handleSubCategoryClick = (month: string, sub_category: string) => {
    setSelectedSubCategory({ month, sub_category });
  };

  const handleBackClick = () => {
    setSelectedSubCategory(null);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && monthGroups.length === 0) {
    return <div className="cashflow-loading">Loading...</div>;
  }

  // Render transaction detail view if a sub-category is selected
  if (selectedSubCategory) {
    const monthGroup = monthGroups.find(mg => mg.month === selectedSubCategory.month);
    const subCategoryGroup = monthGroup?.sub_categories.find(scg => scg.sub_category === selectedSubCategory.sub_category);
    const transactions = subCategoryGroup?.entries || [];

    return (
      <div className="cashflow-monthly-spend">
        <div className="cashflow-category-detail-header">
          <button className="cashflow-category-back-btn" onClick={handleBackClick}>
            ←
          </button>
          <div className="cashflow-category-detail-title">
            <span className="cashflow-category-detail-month">{selectedSubCategory.month}</span>
            <span className="cashflow-category-detail-category">{selectedSubCategory.sub_category}</span>
          </div>
        </div>
        <div className="cashflow-category-transactions">
          {transactions.length === 0 ? (
            <div className="cashflow-empty">No transactions found</div>
          ) : (
            transactions.map(entry => (
              <div
                key={entry.id}
                className="cashflow-category-transaction-row cashflow-category-transaction-row-clickable"
                onClick={() => onEdit && onEdit(entry)}
              >
                <div className="cashflow-category-transaction-main">
                  <div className="cashflow-category-transaction-header">
                    <span className="cashflow-category-transaction-date">{formatDate(entry.created_at)}</span>
                    <span className="cashflow-category-transaction-amount">{formatAmount(entry.amount)}</span>
                  </div>
                  {entry.notes && (
                    <div className="cashflow-category-transaction-notes">{entry.notes}</div>
                  )}
                  {entry.sub_category && (
                    <div className="cashflow-category-transaction-subcategory">{entry.sub_category}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="cashflow-monthly-spend">
      {monthGroups.length === 0 ? (
        <div className="cashflow-empty">No income data found</div>
      ) : (
        <>
          <div className="cashflow-monthly-list">
            {monthGroups.map(monthGroup => {
              const isExpanded = expandedMonths.has(monthGroup.month);
              return (
                <div key={monthGroup.month} className="cashflow-month-group">
                  <button
                    className="cashflow-month-header"
                    onClick={() => toggleMonth(monthGroup.month)}
                  >
                    <span className="cashflow-month-label">{monthGroup.month}</span>
                    <span className="cashflow-month-total">{formatAmount(monthGroup.total)}</span>
                    <span className="cashflow-month-arrow">{isExpanded ? '▼' : '▶'}</span>
                  </button>
                  {isExpanded && (
                    <div className="cashflow-month-content">
                      {monthGroup.sub_categories.map(subCategoryGroup => (
                        <div
                          key={subCategoryGroup.sub_category}
                          className="cashflow-subcategory-row cashflow-category-row-clickable"
                          onClick={() => handleSubCategoryClick(monthGroup.month, subCategoryGroup.sub_category)}
                        >
                          <span className="cashflow-subcategory-name">{subCategoryGroup.sub_category}</span>
                          <span className="cashflow-subcategory-amount">{formatAmount(subCategoryGroup.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="cashflow-load-more-container">
              <button
                className="cashflow-load-more-btn"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IncomeSpend;

